import { useMutation } from '@tanstack/react-query';
import { useRef } from 'react';
import { useNotification } from '../useNotification';
import { useFetchApi } from '../useFetchApi';
import { queryClient } from '../../testUtils';

type SendTestMessageInput = {
  id: string;
  eventType: string;
};

const THROTTLE_DELAY_IN_MS = 2000;

export const useSendTestMessage = ({ webhookId }: { webhookId?: string }) => {
  const fetch = useFetchApi();
  const { showErrorWithResponse } = useNotification();
  const lastCallTimeRef = useRef<number>(0);

  const checkThrottling = (): void => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTimeRef.current;

    if (timeSinceLastCall < THROTTLE_DELAY_IN_MS) {
      const waitTimeInSeconds = Math.ceil(
        (THROTTLE_DELAY_IN_MS - timeSinceLastCall) / 1000
      );
      throw new Error(
        `Please wait ${waitTimeInSeconds} seconds before sending another test message`
      );
    }

    lastCallTimeRef.current = now;
  };

  return useMutation({
    mutationFn: async ({ id, eventType }: SendTestMessageInput) => {
      checkThrottling();

      return fetch(`webhooks/${id}/send-example`, {
        method: 'POST',
        body: JSON.stringify({ eventType }),
      }) as Promise<{ messageId: string }>;
    },
    onError: showErrorWithResponse,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['messageAttempts', webhookId],
      });
    },
  });
};
